'use client';

import { useState } from 'react';
import { DashboardData, UserVoyager } from '@/types/api';
import { Link } from '@tanstack/react-router';

interface UserVoyagersSectionProps {
  data: DashboardData;
}

export default function UserVoyagersSection({ data }: UserVoyagersSectionProps) {
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const voyagers: UserVoyager[] = data?.user_voyagers || [];

  const totalAll = voyagers.length;
  const totalActive = voyagers.filter((v) => v.status === 1).length;
  const totalInactive = voyagers.filter((v) => v.status === 0).length;

  const filteredVoyagers = voyagers.filter((v) => {
    if (filter === 'active') return v.status === 1;
    if (filter === 'inactive') return v.status === 0;
    return true;
  });

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr || dateStr.startsWith('0000')) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getRelativeETA = (arrivalStr?: string) => {
    if (!arrivalStr || arrivalStr.startsWith('0000')) return null;
    try {
      const target = new Date(arrivalStr).getTime();
      const now = new Date().getTime();
      if (isNaN(target)) return null;

      const diffHours = Math.round((target - now) / (1000 * 60 * 60));
      if (diffHours < 0) {
        const absHours = Math.abs(diffHours);
        if (absHours < 24) return `${absHours}h ago`;
        return `${Math.round(absHours / 24)}d ago`;
      }
      if (diffHours < 24) {
        return `in ${diffHours}h`;
      }
      const days = Math.round(diffHours / 24);
      return `in ${days}d`;
    } catch {
      return null;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5 h-full flex flex-col justify-between">
      {/* Header */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">
                Active Voyagers & Selected ETA
              </h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                {totalActive} Active
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Voyager dan estimasi waktu tiba (ETA) pilihan Anda
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {totalAll > 0 && (
              <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg text-xs">
                <button
                  type="button"
                  onClick={() => setFilter('active')}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                    filter === 'active'
                      ? 'bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
                  }`}
                >
                  Active ({totalActive})
                </button>
                <button
                  type="button"
                  onClick={() => setFilter('all')}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                    filter === 'all'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
                  }`}
                >
                  All ({totalAll})
                </button>
                {totalInactive > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilter('inactive')}
                    className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                      filter === 'inactive'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
                    }`}
                  >
                    Off ({totalInactive})
                  </button>
                )}
              </div>
            )}

            <Link
              to="/apps"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm"
            >
              <span>+ Plan</span>
            </Link>
          </div>
        </div>

        {/* Content List */}
        <div className="mt-3">
          {filteredVoyagers.length === 0 ? (
            <div className="py-8 text-center flex flex-col items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {filter === 'active' ? 'Belum ada Voyager Planner yang aktif' : 'Belum ada Voyager Planner'}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 max-w-xs">
                Buat voyager plan untuk memantau rute dan estimasi tiba (ETA).
              </p>
              <div className="mt-3">
                <Link
                  to="/apps"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm"
                >
                  + Buat Planner di Peta
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[290px] pr-1 space-y-2">
              {filteredVoyagers.map((voyager) => {
                const relETA = getRelativeETA(voyager.eta_arrival);
                const isActive = voyager.status === 1;

                return (
                  <div
                    key={voyager.id}
                    className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-800/70 hover:border-brand-200 dark:hover:border-brand-700/50 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
                  >
                    {/* Left: Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            isActive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                          }`}
                        />
                        <span className="font-semibold text-xs text-gray-900 dark:text-white truncate max-w-[180px]">
                          {voyager.name || 'Untitled Voyager'}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[140px]">
                          • {voyager.vessel_name}
                        </span>
                        {voyager.is_hidden ? (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200/60">
                            Hidden
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/60">
                            Shown
                          </span>
                        )}
                      </div>

                      {/* Route & Departure */}
                      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300 font-medium truncate max-w-[170px]">
                          <svg className="w-3.5 h-3.5 text-brand-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span className="truncate">{voyager.route_name || 'Custom Waypoints'}</span>
                        </span>
                        {voyager.distance_nm ? (
                          <span className="text-gray-400 text-[10px]">
                            ({voyager.distance_nm} NM)
                          </span>
                        ) : null}
                        <span>•</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          Dep: <span className="text-gray-700 dark:text-gray-300">{formatDateTime(voyager.departure)}</span>
                        </span>
                      </div>
                    </div>

                    {/* Right: ETA & Action */}
                    <div className="flex items-center justify-between sm:justify-end gap-2.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-800">
                      <div className="text-left sm:text-right">
                        <span className="text-[9px] text-gray-400 uppercase font-semibold tracking-wider block">
                          ETA Arrival
                        </span>
                        {voyager.eta_arrival && !voyager.eta_arrival.startsWith('0000') ? (
                          <div className="flex items-center sm:justify-end gap-1 mt-0.5">
                            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                              {formatDateTime(voyager.eta_arrival)}
                            </span>
                            {relETA && (
                              <span className="text-[10px] px-1 py-0.2 rounded font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200/50">
                                {relETA}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400 italic">Not set</span>
                        )}
                      </div>

                      <Link
                        to="/apps"
                        className="p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                        title="View on Map"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
